import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { getSettingsEditing } from 'client/reducers/managementReducer'
import { useManagement } from 'client/store'
import { setSettingsEditing } from 'client/store/management/managementActions'
import React from 'react'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { PlusIcon } from 'src/editor/shared/icons/plus-icon'
import { Share02Icon } from 'src/editor/shared/icons/share-02-icon'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  FormInterface,
  FormSubEntityTypeEnum,
} from 'src/shared/types/entities/form-interface'
import { getFormSubEntitiesSettingsIcons } from '../../utils/get-form-sub-entities-settings-icons'
import { FormAddNewSubEntitySettings } from './form-add-new-sub-entity-settings'
import { FormSubEntitiesSettingsNestedMenuItem } from './form-sub-entities-settings-nested-menu-item'
import { FormCheckboxSubEntitySettings } from './form-sub-entity-checkbox-settings'
import { FormSubEntityDndItem } from './form-sub-entity-dnd-item'
import { FormFieldSubEntitySettings } from './form-sub-entity-field-settings'
import { FormSubmitButtonSubEntitySettings } from './form-sub-entity-submit-button-settings'

interface FormSubEntitiesSettingsProps {
  entity: FormInterface
}

export const FormSubEntitiesSettings = ({
  entity,
}: FormSubEntitiesSettingsProps) => {
  const { t } = useTypedTranslation()
  const dispatch = useAppDispatch()

  const updateEntity = useUpdateEntity<FormInterface>()

  const editingSubEntityId = useManagement(getSettingsEditing).subEntityId

  const onClose = () => {
    dispatch(
      setSettingsEditing({
        id: entity.id,
        type: entity.type,
        subEntityId: undefined,
      }),
    )
  }

  const onDelete = (id: string) => (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    updateEntity({
      ...entity,
      subEntities: entity.subEntities.filter(subEntity => subEntity.id !== id),
    })
  }

  const onMove = (dragIndex: number, hoverIndex: number) => {
    updateEntity({
      ...entity,
      subEntities: entity.subEntities.map((subEntity, index) =>
        index === dragIndex
          ? entity.subEntities[hoverIndex]
          : index === hoverIndex
            ? entity.subEntities[dragIndex]
            : subEntity,
      ),
    })
  }

  return (
    <div className="flex flex-col gap-2">
      {entity.subEntities.map((subEntity, index) => (
        <>
          {subEntity.type === FormSubEntityTypeEnum.SubmitButton ? (
            <div className="flex items-center gap-2">
              <div className="w-5" />
              <FormSubEntitiesSettingsNestedMenuItem
                title={t('entity_settings.submit_button.header')}
                icon={Share02Icon}
                isOpen={
                  editingSubEntityId === undefined
                    ? undefined
                    : editingSubEntityId === subEntity.id
                }
                onClose={onClose}
              >
                <FormSubmitButtonSubEntitySettings
                  formEntity={entity}
                  submitButtonSubEntity={subEntity}
                />
              </FormSubEntitiesSettingsNestedMenuItem>
            </div>
          ) : (
            <FormSubEntityDndItem
              key={subEntity.id}
              id={subEntity.id}
              index={index}
              onMove={onMove}
            >
              {subEntity.type === FormSubEntityTypeEnum.Field ? (
                <FormSubEntitiesSettingsNestedMenuItem
                  title={getContactSlugName(subEntity.slug, t)}
                  icon={getFormSubEntitiesSettingsIcons(subEntity.slug)}
                  hideDeleteButton={subEntity.slug === ContactSlugEnum.Email}
                  onDelete={onDelete(subEntity.id)}
                  isOpen={
                    editingSubEntityId === undefined
                      ? undefined
                      : editingSubEntityId === subEntity.id
                  }
                  onClose={onClose}
                >
                  <FormFieldSubEntitySettings
                    formEntity={entity}
                    fieldSubEntity={subEntity}
                  />
                </FormSubEntitiesSettingsNestedMenuItem>
              ) : (
                subEntity.type === FormSubEntityTypeEnum.Checkbox && (
                  <FormSubEntitiesSettingsNestedMenuItem
                    title={t('entity_settings.checkbox.header')}
                    icon={CheckSquareIcon}
                    onDelete={onDelete(subEntity.id)}
                    isOpen={
                      editingSubEntityId === undefined
                        ? undefined
                        : editingSubEntityId === subEntity.id
                    }
                    onClose={onClose}
                  >
                    <FormCheckboxSubEntitySettings
                      formEntity={entity}
                      checkboxSubEntity={subEntity}
                    />
                  </FormSubEntitiesSettingsNestedMenuItem>
                )
              )}
            </FormSubEntityDndItem>
          )}
        </>
      ))}
      <SettingsNestedMenu
        withHorizontalLine
        className="m-0"
        menuTitle={t('entity_settings.add_new_element.header')}
        menuButtonClassName="p-0 mb-2"
        menuButtonIcon={() => <PlusIcon className="h-4 w-4" />}
        menuButtonLabel={t('entity_settings.add_new_element.header')}
      >
        <FormAddNewSubEntitySettings entity={entity} />
      </SettingsNestedMenu>
    </div>
  )
}
